import React, { useState, useRef, useEffect } from "react";
import { IMG_CDN_URL } from "../utils/constants";
import { useNavigate } from "react-router-dom";
import { CiCirclePlus, CiCircleCheck } from "react-icons/ci";
import { IoPlayCircleOutline } from "react-icons/io5";
import { movieGenres } from "../utils/movieGeneres";
import { useDispatch, useSelector } from "react-redux";
import { addToWishlist, removeFromWishlist } from "../utils/movieSlice";
import Modal from "./Modal";

const MovieCardShimmer = () => {
	return (
		<div className="movie-card-shimmer animate-pulse">
			<div className="bg-neutral-500 opacity-30 rounded-md aspect-video"></div>
		</div>
	);
};

const MovieCard = ({ movie, posterPath, lastList, onHoverChange }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const wishlist = useSelector((state) => state.movies.wishlist);
	const [isHover, setIsHover] = useState(false);
	const [isVisible, setIsVisible] = useState(false); // New state for controlling opacity
	const [imgWidth, setImgWidth] = useState(0);
	const [isScalingDown, setIsScalingDown] = useState(false);
	const [loading, setLoading] = useState(true);
	const [hoverLoading, setHoverLoading] = useState(true);
	const cardRef = useRef(null);
	const hoverTimeoutRef = useRef(null);
	const visibilityTimeoutRef = useRef(null); // New timeout ref for opacity transition
	const scaleDownTimeOutRef = useRef(null);
	const movieCardImgRef = useRef(null);
	const movieHoverCardImgRef = useRef(null);
	const movieGenereIds = movie?.genre_ids?.slice(0, 2) || [];
	const [showVideoModal, setShowVideoModal] = useState(false);

	// Add this useEffect to measure the actual card width
	useEffect(() => {
		// Function to update imgWidth based on card dimensions
		const updateCardWidth = () => {
			if (cardRef.current) {
				// Set width to the actual rendered width of the card
				const cardWidth = cardRef.current.offsetWidth;
				setImgWidth(cardWidth);
			}
		};

		// Run immediately
		updateCardWidth();

		// Also run when window resizes
		window.addEventListener("resize", updateCardWidth);

		// Clean up
		return () => {
			window.removeEventListener("resize", updateCardWidth);
		};
	}, []);

	useEffect(() => {
		// Preload hover image when component mounts
		if (posterPath) {
			const img = new Image();
			img.src = IMG_CDN_URL + posterPath;

			// Optional: listen for the image load if you want to track when it's ready
			img.onload = () => {
				// Image is now cached by the browser
			};
		}
	}, [posterPath]);

	const checkIfRendered = () => {
		const img = movieCardImgRef.current;

		if (img && img.complete && img.naturalWidth > 0) {
			setLoading(false);
		} else {
			requestAnimationFrame(checkIfRendered);
		}
	};

	const checkIfRenderedHover = () => {
		const hoverImg = movieHoverCardImgRef?.current;

		if (hoverImg && hoverImg.complete && hoverImg.naturalWidth > 0) {
			setHoverLoading(false);
		} else {
			requestAnimationFrame(checkIfRenderedHover);
		}
	};

	useEffect(() => {
		const handleImageLoad = () => {
			requestAnimationFrame(checkIfRendered);
		};

		if (movieCardImgRef.current) {
			movieCardImgRef.current.addEventListener("load", handleImageLoad);
		}

		return () => {
			if (movieCardImgRef.current) {
				movieCardImgRef.current.removeEventListener("load", handleImageLoad);
			}
		};
	}, []);

	// Reset hover loading state when hover state changes
	useEffect(() => {
		if (isHover) {
			setHoverLoading(true); // Reset to loading state when hover begins

			// Set visibility after a small delay to ensure smooth transition
			visibilityTimeoutRef.current = setTimeout(() => {
				setIsVisible(true);
			}, 50);
		} else {
			setIsVisible(false);
		}

		return () => {
			clearTimeout(visibilityTimeoutRef.current);
		};
	}, [isHover]);

	useEffect(() => {
		if (isHover && movieHoverCardImgRef.current) {
			const handleImageLoad = () => {
				requestAnimationFrame(checkIfRenderedHover);
			};

			movieHoverCardImgRef.current.addEventListener("load", handleImageLoad);

			// Check if image is already loaded from cache
			if (movieHoverCardImgRef.current.complete) {
				requestAnimationFrame(checkIfRenderedHover);
			}

			return () => {
				if (movieHoverCardImgRef.current) {
					movieHoverCardImgRef.current.removeEventListener(
						"load",
						handleImageLoad
					);
				}
			};
		}
	}, [isHover]);

	// If movie is not loaded, show shimmer
	if (!movie) {
		return <MovieCardShimmer />;
	}

	// Check if the current movie is in the wishlist
	const isMovieInWishlist = wishlist.some((m) => m.id === movie?.id);

	const handleMovieClick = (e) => {
		// Ensure we don't navigate if clicking on interactive elements
		if (
			e.target.closest(".wishlist-icon") ||
			e.target.closest("[data-play-button]") ||
			e.target.classList.contains("wishlist-icon")
		) {
			return;
		}

		e.preventDefault();
		e.stopPropagation();

		try {
			if (e.metaKey || e.ctrlKey) {
				window.open(`/movie/${movie.id}`, "_blank");
			} else {
				navigate(`/movie/${movie.id}`);
			}
		} catch (error) {
			console.error("Navigation error:", error);
		}
	};

	const handleMouseEnter = () => {
		clearTimeout(scaleDownTimeOutRef.current);
		clearTimeout(hoverTimeoutRef.current);

		hoverTimeoutRef.current = setTimeout(() => {
			setIsHover(true);
			onHoverChange(true);
		}, 300);
	};

	const handleMouseLeave = () => {
		clearTimeout(hoverTimeoutRef.current);
		clearTimeout(visibilityTimeoutRef.current);

		setIsScalingDown(true);
		setIsVisible(false); // Start fading out before removing

		scaleDownTimeOutRef.current = setTimeout(() => {
			setIsHover(false);
			onHoverChange(false);
			setIsScalingDown(false);
		}, 300);
	};

	const handleMouseLeaveMain = () => {
		clearTimeout(hoverTimeoutRef.current);
	};

	const handleWishlistToggle = (e) => {
		e.preventDefault();
		e.stopPropagation();

		if (isMovieInWishlist) {
			dispatch(removeFromWishlist(movie.id));
		} else {
			dispatch(addToWishlist(movie));
		}
	};

	const handlePlayClick = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setShowVideoModal(true);
	};

	const handleCloseModal = () => {
		setShowVideoModal(false);
	};

	const getTransformOrigin = () => {
		if (!cardRef.current) return "center";

		const rect = cardRef.current.getBoundingClientRect();
		const windowWidth = window.innerWidth;

		// Get the left and right edge positions of the card
		const leftEdgePosition = rect.left;
		const rightEdgePosition = rect.right;

		// Left margin threshold (adjust as needed)
		const leftThreshold = 50;

		// Right margin threshold (adjust as needed)
		const rightThreshold = windowWidth - 50;

		if (leftEdgePosition < leftThreshold) {
			return "left";
		} else if (rightEdgePosition > rightThreshold) {
			return "right";
		} else {
			return "center";
		}
	};

	return (
		<div ref={cardRef} className="relative">
			<div className={`${loading ? "block" : "hidden"}`}>
				<MovieCardShimmer />
			</div>
			<div
				className={`${
					loading ? "hidden" : "block"
				} movie-card h-full cursor-pointer relative z-10`}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeaveMain}
				onClick={handleMovieClick}
			>
				<img
					alt="Movie Card"
					src={IMG_CDN_URL + posterPath}
					className="rounded-md w-full h-full object-cover"
					ref={movieCardImgRef}
					onClick={handleMovieClick}
				/>
			</div>
			{isHover && (
				<div
					className={`movie-card-hover cursor-pointer absolute z-[100] transition-all duration-300 ease-in-out ${
						isScalingDown ? "scale-down" : ""
					}`}
					style={{
						top: lastList ? "-3%" : "0",
						transformOrigin: getTransformOrigin(),
					}}
					onMouseLeave={handleMouseLeave}
					onClick={handleMovieClick}
				>
					<div>
						<div
							className="bg-black aspect-video"
							style={{ minWidth: imgWidth, minHeight: (imgWidth * 9) / 16 }}
						>
							<img
								alt="Movie Card Hover"
								src={IMG_CDN_URL + posterPath}
								className="rounded-md rounded-b-none w-full"
								ref={movieHoverCardImgRef}
								onClick={handleMovieClick}
							/>
						</div>

						<div
							className={`movie-hover-card-description flex flex-col gap-2 bg-[#141414] text-white p-4 rounded-md rounded-t-none
							${isScalingDown ? "fadeout" : ""}
							`}
						>
							<div className="flex gap-2">
								<IoPlayCircleOutline
									size={24}
									className="text-gray-400 hover:text-white cursor-pointer"
									alt="Play button"
									data-play-button="true"
									onClick={handlePlayClick}
								/>
								{isMovieInWishlist ? (
									<CiCircleCheck
										size={24}
										className="text-green-400 hover:text-green-500 cursor-pointer wishlist-icon"
										onClick={handleWishlistToggle}
									/>
								) : (
									<CiCirclePlus
										size={24}
										className="text-gray-400 hover:text-white cursor-pointer wishlist-icon"
										onClick={handleWishlistToggle}
									/>
								)}
							</div>
							<div onClick={handleMovieClick} className="cursor-pointer">
								<h3 className="text-xs 2xl:text-base">{movie.title}</h3>
							</div>
							<div
								className="flex flex-wrap gap-1 text-xs 2xl:text-base"
								onClick={handleMovieClick}
							>
								{movieGenereIds.map((id, idx) => (
									<p key={id}>
										{idx === 1
											? movieGenres[id]
											: `${movieGenres[id]} ${
													movieGenereIds.length > 1 ? "|" : ""
											  }`}
									</p>
								))}
							</div>
						</div>
					</div>
				</div>
			)}

			{showVideoModal && (
				<Modal movieId={movie?.id} onClose={handleCloseModal} />
			)}
		</div>
	);
};

export default MovieCard;
